import { useEffect, useMemo, useRef, useCallback } from 'react';
import L from 'leaflet';
import type { Vendor } from '../../../types';
import { useMarketData } from '../../../hooks/useMarketData';
import { useEditorModeContext, getSelectedUids } from '../../../context/EditorModeContext.utils';
import { buildMarkerSvg, type MarkerType } from '../markerSvg';
import { DEFAULT_CATEGORY_COLOR } from '../../../utils/categoryColors';
import styles from './ScaledMarkerLayer.module.css';
import { useMarkerInteraction } from './useMarkerInteraction';
import { useMarkerSync } from './useMarkerSync';
import { useZoomAnimation } from './useZoomAnimation';
import { useLasso } from './useLasso';

interface Props {
  map: L.Map;
  /**
   * Stable uid of the currently-selected vendor. Drives marker styling —
   * active marker stays full-opacity, others fade.
   */
  selectedVendorUid?: string | null | undefined;
  /**
   * Opaque key that changes every time the caller re-focuses the current
   * vendor (e.g. VendorDetail → "Show on map"). Bumping this replays
   * the focus ping animation even when `selectedVendorUid` is unchanged.
   */
  focusKey?: string | null | undefined;
  /** Fired when a vendor marker is tapped (normal mode only). */
  onVendorTap?: ((vendorUid: string) => void) | undefined;
  /**
   * Notifies the parent when a previously-selected vendor falls out of
   * the filtered set so it can clear the URL/state. Optional.
   */
  onSelectionLost?: (() => void) | undefined;
}

export function ScaledMarkerLayer({
  map,
  selectedVendorUid = null,
  focusKey = null,
  onVendorTap,
  onSelectionLost,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markerElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const placeholderElsRef = useRef<Map<string, HTMLElement>>(new Map());
  // Cached label refs (keyed by the same uids as the marker maps). Leaflet
  // fires `move` continuously during pan — querying the label via
  // `querySelector` on every move adds up at 150 markers. Set on creation,
  // cleared on removal, read in the hot paths below.
  const markerLabelElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const placeholderLabelElsRef = useRef<Map<string, HTMLElement>>(new Map());
  const { filteredVendors, categories, positions, vendors, nameFor } = useMarketData();
  const {
    isEditing,
    mode,
    movePositionsGroup,
    selectPosition,
    toggleInSelection,
    addToSelection,
  } = useEditorModeContext();
  const selectedUids = useMemo(() => getSelectedUids(mode), [mode]);
  const previewElRef = useRef<HTMLElement | null>(null);

  // Stable refs for the callback-prop API so the per-marker DOM
  // listeners always see the latest closure without re-attaching.
  const onVendorTapRef = useRef<typeof onVendorTap>(onVendorTap);
  const onSelectionLostRef = useRef<typeof onSelectionLost>(onSelectionLost);
  const movePositionsGroupRef = useRef(movePositionsGroup);
  const selectPositionRef = useRef(selectPosition);
  const toggleInSelectionRef = useRef(toggleInSelection);
  const addToSelectionRef = useRef(addToSelection);
  // The lasso handler binds once per edit-mode flip but needs live data at
  // pointerup. Refs keep the latest arrays in view without retearing the
  // event listener on every vendor/position mutation.
  const filteredVendorsRef = useRef(filteredVendors);
  const positionsRef = useRef(positions);
  const vendorsRef = useRef(vendors);
  // Live selection ref for multi-drag hit-testing inside pointer event
  // handlers (which bind once and must not go stale).
  const selectedUidsRef = useRef(selectedUids);
  // Maps position uid → marker DOM element (vendor or placeholder) for
  // O(1) element lookups during multi-drag without DOM queries.
  const positionUidToElRef = useRef<Map<string, HTMLElement>>(new Map());
  useEffect(() => { onVendorTapRef.current = onVendorTap; }, [onVendorTap]);
  useEffect(() => { onSelectionLostRef.current = onSelectionLost; }, [onSelectionLost]);
  useEffect(() => { movePositionsGroupRef.current = movePositionsGroup; }, [movePositionsGroup]);
  useEffect(() => { selectPositionRef.current = selectPosition; }, [selectPosition]);
  useEffect(() => { toggleInSelectionRef.current = toggleInSelection; }, [toggleInSelection]);
  useEffect(() => { addToSelectionRef.current = addToSelection; }, [addToSelection]);
  useEffect(() => { filteredVendorsRef.current = filteredVendors; }, [filteredVendors]);
  useEffect(() => { positionsRef.current = positions; }, [positions]);
  useEffect(() => { vendorsRef.current = vendors; }, [vendors]);
  useEffect(() => { selectedUidsRef.current = selectedUids; }, [selectedUids]);

  const categoryById = useMemo(
    () => new Map(categories.map(c => [c.id, c])),
    [categories],
  );

  const getCategoryColor = useCallback(
    (categoryId: string): string => categoryById.get(categoryId)?.color ?? DEFAULT_CATEGORY_COLOR,
    [categoryById],
  );

  const getSvg = useCallback(
    (type: MarkerType, categoryId: string, categoryColor: string): string =>
      buildMarkerSvg(type, categoryId, categoryColor),
    [],
  );

  // O(1) lookups in zoom/pan hot paths. Without these, every marker rebound
  // per zoom event is O(n) — for ~100 markers × ~60 zoomanim frames/sec,
  // that's 600k ops/sec just to find what we already have indexed.
  //
  // Indexed by `positionUid` (not `vendor.uid`) so vendors fanned out across
  // multiple booths — same vendor row matched against several Position
  // records sharing a `position_id` — get one entry per booth. Indexing by
  // `vendor.uid` would collide and we'd lose all but one of those copies.
  const vendorByPositionUid = useMemo(
    () => new Map(filteredVendors.map(v => [v.positionUid, v])),
    [filteredVendors],
  );
  const positionByUid = useMemo(
    () => new Map(positions.map(p => [p.uid, p])),
    [positions],
  );
  const positionByUidRef = useRef(positionByUid);
  useEffect(() => { positionByUidRef.current = positionByUid; }, [positionByUid]);

  const { attachDragOrSelect } = useMarkerInteraction({
    map,
    selectedUidsRef,
    positionByUidRef,
    positionUidToElRef,
    movePositionsGroupRef,
    toggleInSelectionRef,
    selectPositionRef,
  });

  // Must run before useMarkerSync so containerRef.current is set when the
  // sync effect fires — even when vendor data is already loaded (e.g. after
  // React StrictMode's simulated unmount/remount cycle).
  useEffect(() => {
    const pane = map.getPane('overlayPane');
    if (!pane) return;
    const container = document.createElement('div');
    container.className = styles.scaledMarkersContainer!;
    pane.appendChild(container);
    containerRef.current = container;
    return () => {
      container.remove();
      containerRef.current = null;
    };
  }, [map]);

  const { updateMarkerPositions, updateMarkerPositionsThrottled, rafIdRef, markersSettledTick } = useMarkerSync({
    map,
    containerRef,
    markerElsRef,
    placeholderElsRef,
    markerLabelElsRef,
    placeholderLabelElsRef,
    positionUidToElRef,
    previewElRef,
    filteredVendors,
    positions,
    vendors,
    vendorByPositionUid,
    positionByUid,
    isEditing,
    mode,
    getSvg,
    getCategoryColor,
    nameFor,
    onVendorTapRef,
    attachDragOrSelect,
  });

  const { animateMarkerZoom, clearZoomTransition } = useZoomAnimation({
    map,
    vendorByPositionUid,
    positionByUid,
    markerElsRef,
    placeholderElsRef,
    markerLabelElsRef,
    placeholderLabelElsRef,
  });

  useLasso({
    isEditing,
    map,
    filteredVendorsRef,
    positionsRef,
    vendorsRef,
    addToSelectionRef,
  });

  useEffect(() => {
    map.on('move', updateMarkerPositionsThrottled);
    map.on('moveend', updateMarkerPositions);
    map.on('viewreset', updateMarkerPositions);
    map.on('zoomanim', animateMarkerZoom);
    map.on('zoomend', clearZoomTransition);
    map.on('zoomend', updateMarkerPositions);
    updateMarkerPositions();
    return () => {
      map.off('move', updateMarkerPositionsThrottled);
      map.off('moveend', updateMarkerPositions);
      map.off('viewreset', updateMarkerPositions);
      map.off('zoomanim', animateMarkerZoom);
      map.off('zoomend', clearZoomTransition);
      map.off('zoomend', updateMarkerPositions);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  // rafIdRef is a stable ref — intentionally omitted from deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, updateMarkerPositions, updateMarkerPositionsThrottled, animateMarkerZoom, clearZoomTransition]);

  // Paint the editor-mode (multi-)selection ring. Derives from `selectedUids`
  // on the EditorModeContext — covers both size-1 (single edit) and size>=2
  // (multi-select for Align/Distribute). Looks up vendor markers by
  // positionUid; placeholder markers are already keyed by positionUid.
  useEffect(() => {
    markerElsRef.current.forEach((el) => {
      const positionUid = el.dataset.positionUid;
      if (positionUid && selectedUids.has(positionUid)) {
        el.classList.add(styles.multiSelected!);
      } else {
        el.classList.remove(styles.multiSelected!);
      }
    });
    placeholderElsRef.current.forEach((el, uid) => {
      if (selectedUids.has(uid)) el.classList.add(styles.multiSelected!);
      else el.classList.remove(styles.multiSelected!);
    });
  }, [selectedUids, filteredVendors, positions, isEditing]);

  // Selection styling driven by the `selectedVendorUid` prop. The parent
  // owns the current selection (see AppShell + useVendorSelection); we
  // just paint markers accordingly. Class-driven (not inline opacity) so
  // the CSS layer owns the visual treatment and can evolve independently.
  useEffect(() => {
    // Marker map is keyed by positionUid; check vendorUid via the dataset
    // attribute so a vendor fanned out across multiple booths gets the
    // active styling on every one of its markers.
    markerElsRef.current.forEach((el) => {
      // Clear any legacy inline opacity that earlier versions used.
      el.style.opacity = '';
      if (selectedVendorUid == null) {
        el.classList.remove(styles.activeMarker!);
        el.classList.remove(styles.fadedMarker!);
        return;
      }
      if (el.dataset.vendorUid === selectedVendorUid) {
        el.classList.add(styles.activeMarker!);
        el.classList.remove(styles.fadedMarker!);
      } else {
        el.classList.add(styles.fadedMarker!);
        el.classList.remove(styles.activeMarker!);
      }
    });
    // markersSettledTick: re-apply once chunked marker creation finishes so
    // markers created in later frames still get active/faded styling.
  }, [selectedVendorUid, filteredVendors, markersSettledTick]);

  // Fire the `justFocused` class on the active marker(s) so the CSS ping
  // animation plays. Replays on every focusKey bump (covers "Show on
  // map" re-focus), and also retries after filteredVendors updates so
  // a cold-load `/listing/x` deep-link still pings once the marker DOM
  // is present. `pingedKeyRef` dedupes so we don't re-ping on unrelated
  // filter churn. All booths are pinged simultaneously for multi-position
  // vendors so every marker pulses when the user hits "Show on map".
  const pingedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedVendorUid) {
      pingedKeyRef.current = null;
      return;
    }
    const pingKey = `${selectedVendorUid}|${focusKey ?? ''}`;
    if (pingKey === pingedKeyRef.current) return;
    const els: HTMLElement[] = [];
    markerElsRef.current.forEach((el) => {
      if (el.dataset.vendorUid === selectedVendorUid) els.push(el);
    });
    if (els.length === 0) return; // markers not in DOM yet; retry when filteredVendors commits
    for (const el of els) {
      el.classList.remove(styles.justFocused!);
      void el.offsetWidth;
      el.classList.add(styles.justFocused!);
    }
    pingedKeyRef.current = pingKey;
    const timer = window.setTimeout(() => {
      for (const el of els) el.classList.remove(styles.justFocused!);
    }, 800);
    return () => window.clearTimeout(timer);
    // markersSettledTick: retry the ping once chunked creation finishes (a
    // deep-linked vendor's marker may be created several frames after mount).
  }, [selectedVendorUid, focusKey, filteredVendors, markersSettledTick]);

  // If the previously-selected vendor is no longer visible, ask the parent
  // to clear its selection (so the URL/state stays in sync).
  // Guard: skip when vendors haven't loaded yet (vendors.length === 0) so a
  // deep-linked UID isn't evicted during the brief window between loading
  // becoming false and MarketDataContext's SET_DATA useEffect firing.
  useEffect(() => {
    if (selectedVendorUid == null) return;
    if (vendors.length === 0) return;
    const stillVisible = filteredVendors.some((v: Vendor) => v.uid === selectedVendorUid);
    if (!stillVisible) {
      onSelectionLostRef.current?.();
    }
  }, [filteredVendors, selectedVendorUid, vendors]);

  return null;
}
